// Administrace materiálů
import { icon } from './icons.js';

class MaterialsAdmin {
  constructor() {
    this.baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
      ? 'http://localhost:1234/api' 
      : '/api';
    this.materials = {};
    this.init();
  }

  // Získat informace o roli uživatele
  getCurrentUserRole() {
    try {
      const userData = localStorage.getItem('currentUser');
      if (userData) {
        const user = JSON.parse(userData);
        return user.role;
      }
    } catch (error) {
      console.error('Chyba při načítání role uživatele:', error);
    }
    return null;
  }

  // Kontrola, zda je uživatel admin
  isAdmin() {
    return this.getCurrentUserRole() === 'admin';
  }

  // Skrýt/zobrazit UI prvky podle role uživatele
  handleRoleBasedUI() {
    const isAdmin = this.isAdmin();
    const adminButtons = document.getElementById('materials-admin-buttons');
    
    if (adminButtons) {
      if (isAdmin) {
        adminButtons.classList.remove('hidden');
      } else {
        adminButtons.classList.add('hidden');
      }
    }
  }

  async init() {
    try {
      // Nastavit loading text do info boxu
      const el = document.getElementById('materials-last-saved-info');
      if (el && !el.textContent.trim()) {
        el.textContent = '(načítám z API...)';
      }
      
      // Skrýt/zobrazit admin tlačítka podle role
      this.handleRoleBasedUI();
      
      await this.loadMaterials();
      this.renderMaterials();
      // Inicializace formuláře s materiály
      this.updateMaterialsForm();
    } catch (error) {
      console.error('Chyba při inicializaci:', error);
      this.showError('Chyba při načítání materiálů');
      
      // Zachovat původní text v info boxu pokud existuje
      const el = document.getElementById('materials-last-saved-info');
      if (el) {
        const currentText = el.textContent.trim();
        if (!currentText || currentText === '(načítám z API...)' || currentText.includes('chyba')) {
          el.textContent = '(chyba načtení z API)';
        }
      }
    }
  }

  async loadMaterials() {
    try {
      const response = await fetch(`${this.baseUrl}/materials`);
      const result = await response.json();
      
      if (result.success) {
        // Nový formát dat s metadata wrapper
        this.materials = result.data.materials;
        // Nastavit informaci o posledním uložení
        const el = document.getElementById('materials-last-saved-info');
        if (el && result.data.lastUpdated) {
          const lastUpdated = new Date(result.data.lastUpdated);
          const userName = result.data.lastUpdatedBy || 'neznámý';
          const storageType = result.data.storageType || 'file';
          const storageText = storageType === 'redis' ? 'cloud databáze' : 'lokální soubor';
          el.textContent = `Poslední úpravu provedl uživatel ${userName} ${lastUpdated.toLocaleString('cs-CZ')} (${storageText})`;
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Chyba při načítání materiálů:', error);
      // Fallback text do info boxu - ale zachováme původní text pokud existuje
      const el = document.getElementById('materials-last-saved-info');
      if (el) {
        // Pokud je element prázdný nebo obsahuje chybovou zprávu, nastavíme fallback
        const currentText = el.textContent.trim();
        if (!currentText || currentText === '(načítám z API...)' || currentText.includes('chyba')) {
          el.textContent = '(chyba načtení z API)';
        }
        // Jinak ponecháme původní text - neztratíme informaci o centrální správě
      }
      
      // Nevyhazujeme error, aby se aplikace mohla dál spustit
      // throw error;
    }
  }

  renderMaterials() {
    const container = document.getElementById('materials-container');
    if (!container) return;

    const isAdmin = this.isAdmin();
    const totalMaterials = Object.values(this.materials).reduce((sum, materials) => sum + materials.length, 0);

    if (totalMaterials === 0) {
      container.innerHTML = `
        <div class="text-center py-12">
          ${icon('inbox', 'mx-auto h-12 w-12 text-ink-faint mb-3')}
          <h3 class="text-base font-medium text-ink-muted mb-1">Zatím žádné materiály</h3>
          <p class="text-sm text-ink-faint">Začněte přidáním prvního materiálu tlačítkem „Přidat materiál".</p>
        </div>
      `;
      this.initTooltips();
      return;
    }

    let html = '';

    // Low-stock banner – pouze pokud jsou nějaké nízké zásoby
    const lowStock = this.computeLowStock();
    if (lowStock.length > 0) {
      html += `
        <div class="mb-6 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" id="low-stock-banner">
          <div class="flex items-center gap-2 text-sm text-amber-800">
            ${icon('exclamation-triangle', 'w-5 h-5 text-amber-500')}
            <span><strong>Nízké zásoby:</strong> ${lowStock.length} ${lowStock.length === 1 ? 'materiál' : (lowStock.length < 5 ? 'materiály' : 'materiálů')}</span>
          </div>
          <div class="flex items-center gap-2">
            <button class="text-xs font-medium text-amber-800 hover:text-amber-900 underline" onclick="materialsAdmin.toggleLowStockFilter()">
              <span id="low-stock-filter-label">Zobrazit pouze nízké</span>
            </button>
          </div>
        </div>
      `;
    }

    for (const [categoryId, categoryMaterials] of Object.entries(this.materials)) {
      if (categoryMaterials.length === 0) continue;
      html += `
        <section id="papiry-${categoryId}" class="mb-6 last:mb-0 scroll-mt-24" data-category="${categoryId}">
          <header class="flex items-center gap-2 mb-2 px-1">
            ${icon('folder', 'w-4 h-4 text-brand-500')}
            <h3 class="text-sm font-semibold text-ink">${this.getCategoryName(categoryId)}</h3>
            <span class="inline-flex items-center px-1.5 py-0.5 rounded bg-ink-50 text-ink-muted text-[11px] font-medium">${categoryMaterials.length}</span>
          </header>
          <div class="overflow-x-auto border border-ink-100 rounded-lg">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="bg-surface-1 text-ink-muted">
                  <th class="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider">Název</th>
                  <th class="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider">Cena / arch</th>
                  <th class="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider">Skladem</th>
                  <th class="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider">Min</th>
                  <th class="px-4 py-2 text-center text-[11px] font-semibold uppercase tracking-wider">Gramáž</th>
                  <th class="px-4 py-2 text-center text-[11px] font-semibold uppercase tracking-wider">Rozměry</th>
                  <th class="px-4 py-2 text-center text-[11px] font-semibold uppercase tracking-wider">Obrátka</th>
                  <th class="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider">Akce</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-ink-50">
      `;
      categoryMaterials.forEach(material => {
        const isLowStock = this.isMaterialLowStock(material);
        const stockColor = (parseInt(material.skladem) || 0) === 0
          ? 'bg-red-100 text-red-700'
          : (isLowStock ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700');
        const available = (parseInt(material.skladem) || 0) - (parseInt(material.reservedCount) || 0);
        const reservedCount = parseInt(material.reservedCount) || 0;
        const lowStockClass = isLowStock ? 'data-low-stock="true"' : '';
        html += `
          <tr class="hover:bg-surface-1 transition-colors" ${lowStockClass}>
            <td class="px-4 py-2 whitespace-nowrap font-medium text-ink">${material.name}</td>
            <td class="px-4 py-2 text-right">
              ${isAdmin
                ? `<input type="number" value="${material.price}" step="0.01" min="0" onchange="materialsAdmin.updatePrice('${categoryId}', '${material.id}', this.value)" class="w-24 text-right px-2 py-1 border border-ink-100 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm tabular-nums" title="Změnit cenu">`
                : `<span class="text-ink-muted tabular-nums">${Number(material.price).toFixed(2)} Kč</span>`
              }
            </td>
            <td class="px-4 py-2 text-right">
              <div class="flex flex-col items-end">
                ${isAdmin
                  ? `<input type="number" value="${material.skladem || 0}" min="0" onchange="materialsAdmin.updateStock('${categoryId}', '${material.id}', this.value)" class="w-20 text-right px-2 py-1 border border-ink-100 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm tabular-nums" title="Změnit skladové množství">`
                  : `<span class="inline-flex items-center px-2 py-0.5 rounded ${stockColor} text-xs font-medium tabular-nums">${material.skladem || 0}</span>`
                }
                ${isAdmin ? `<span class="text-[10px] text-ink-faint mt-0.5">Dostupné: ${available}</span>` : `<span class="text-[10px] text-ink-faint mt-0.5">Dostupné: ${available}</span>`}
                ${reservedCount > 0 ? `<span class="text-[10px] text-amber-600">Rezervováno: ${reservedCount}</span>` : ''}
              </div>
            </td>
            <td class="px-4 py-2 text-right">
              ${isAdmin
                ? `<input type="number" value="${material.minStock || 0}" min="0" onchange="materialsAdmin.updateMinStock('${categoryId}', '${material.id}', this.value)" class="w-16 text-right px-2 py-1 border border-ink-100 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm tabular-nums" title="Minimální skladové množství">`
                : `<span class="text-ink-muted tabular-nums">${material.minStock || 0}</span>`
              }
            </td>
            <td class="px-4 py-2 text-center text-ink-muted tabular-nums">${material.weight} <span class="text-ink-faint text-xs">g/m²</span></td>
            <td class="px-4 py-2 text-center text-ink-faint text-xs tabular-nums">${material.width}×${material.height} mm</td>
            <td class="px-4 py-2 text-center">
              ${material.canFlip
                ? `<span class="inline-flex items-center gap-1 text-success text-xs font-medium">${icon('check', 'w-3.5 h-3.5')}ano</span>`
                : `<span class="text-ink-faint text-xs">—</span>`
              }
            </td>
            <td class="px-4 py-2 text-right">
              <div class="flex justify-end gap-1">
                <button class="p-1.5 rounded-md hover:bg-brand-50 text-ink-muted hover:text-brand transition-colors" onclick="materialsAdmin.showMovementsModal('${categoryId}', '${material.id}')" title="Skladové pohyby">
                  ${icon('arrow-path', 'w-4 h-4')}
                </button>
                ${isAdmin
                  ? `<button class="p-1.5 rounded-md hover:bg-brand-50 text-ink-muted hover:text-brand transition-colors" onclick="materialsAdmin.editMaterial('${categoryId}', '${material.id}')" title="Upravit">
                      ${icon('pencil-square', 'w-4 h-4')}
                    </button>
                    <button class="p-1.5 rounded-md hover:bg-danger-soft text-ink-muted hover:text-danger transition-colors" onclick="materialsAdmin.deleteMaterial('${categoryId}', '${material.id}')" title="Smazat">
                      ${icon('trash', 'w-4 h-4')}
                    </button>`
                  : `<span class="text-ink-faint text-xs">jen pro čtení</span>`
                }
              </div>
            </td>
          </tr>
        `;
      });
      html += `
              </tbody>
            </table>
          </div>
        </section>
      `;
    }

    container.innerHTML = html;
    this.initTooltips();
    this.renderCategoriesNav();
    this.initCategoriesNav();
  }

  /**
   * Vykreslí boční navigaci kategoriemi papírů (pouze kategorie, které mají alespoň jeden materiál).
   */
  renderCategoriesNav() {
    const nav = document.getElementById('papiry-nav');
    if (!nav) return;

    const linksHtml = Object.entries(this.materials)
      .filter(([, items]) => items.length > 0)
      .map(([categoryId, items]) => `
        <a href="#papiry-${categoryId}" class="settings-nav__link" data-category="${categoryId}">
          <span class="flex-1 truncate">${this.getCategoryName(categoryId)}</span>
          <span class="text-[11px] text-ink-faint tabular-nums">${items.length}</span>
        </a>
      `).join('');

    nav.innerHTML = `
      <div class="settings-nav__group">
        <div class="settings-nav__title">Kategorie papírů</div>
        ${linksHtml}
      </div>
    `;
  }

  /**
   * Scroll-spy: plynulý skok na kategorii po kliknutí + zvýraznění aktivní kategorie
   * podle pozice scrollu. Stejný pattern jako initSettingsNav v app.js.
   */
  initCategoriesNav() {
    const nav = document.getElementById('papiry-nav');
    if (!nav) return;

    const links = Array.from(nav.querySelectorAll('.settings-nav__link'));
    if (!links.length) return;

    const targets = links
      .map((link) => {
        const id = link.getAttribute('href')?.replace('#', '');
        const el = id ? document.getElementById(id) : null;
        return el ? { link, el } : null;
      })
      .filter(Boolean);
    if (!targets.length) return;

    const setActive = (activeLink) => {
      links.forEach((l) => l.classList.remove('is-active'));
      if (activeLink) activeLink.classList.add('is-active');
    };

    // Výchozí aktivní = první kategorie
    setActive(targets[0].link);

    // Klik na odkaz = plynulé odskrolování na sekci
    links.forEach((link) => {
      link.addEventListener('click', (e) => {
        const id = link.getAttribute('href')?.replace('#', '');
        const el = id ? document.getElementById(id) : null;
        if (!el) return;
        e.preventDefault();
        setActive(link);
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    // Odpojíme starý observer při opětovném renderu
    if (this._categoriesObserver) {
      this._categoriesObserver.disconnect();
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((en) => en.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (!visible.length) return;
          const topId = visible[0].target.id;
          const match = targets.find((t) => t.el.id === topId);
          if (match) setActive(match.link);
        },
        { rootMargin: '-96px 0px -60% 0px', threshold: 0 }
      );
      targets.forEach((t) => observer.observe(t.el));
      this._categoriesObserver = observer;
    }
  }

  getCategoryName(categoryId) {
    const categoryNames = {
      'ofset': 'Ofsetový papír',
      'laserova': 'Do laserové tiskárny',
      'krida-leskla': 'Lesklá křída',
      'krida-matna': 'Matná křída',
      'recyklovane': 'Recyklované a ekologické papíry',
      'lepenky': 'Lepenky',
      'samolepici': 'Samolepící papíry',
      'kartony': 'Kartóny',
      'specialni': 'Speciální grafické papíry'
    };
    return categoryNames[categoryId] || 'Ostatní';
  }

  async updatePrice(categoryId, materialId, newPrice) {
    try {
      const response = await fetch(`${this.baseUrl}/materials/${categoryId}/${materialId}/price`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ price: parseFloat(newPrice) })
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.showError('Nejste přihlášeni. Přihlaste se prosím jako administrátor.');
          return;
        }
        if (response.status === 403) {
          this.showError('Nemáte oprávnění k úpravě cen. Pouze administrátoři mohou upravovat ceny materiálů.');
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        this.showSuccess(`Cena materiálu byla aktualizována na ${newPrice} Kč`);
        // Aktualizovat lokální data
        const material = this.materials[categoryId].find(m => m.id === materialId);
        if (material) {
          material.price = parseFloat(newPrice);
        }
        // Aktualizovat formulář
        this.updateMaterialsForm();
      } else {
        throw new Error(result.error || 'Neznámá chyba');
      }
    } catch (error) {
      console.error('Chyba při aktualizaci ceny:', error);
      if (error.name === 'SyntaxError') {
        this.showError('Nejste přihlášeni nebo nemáte oprávnění. Přihlaste se prosím jako administrátor.');
      } else {
        this.showError(`Chyba při aktualizaci ceny: ${error.message}`);
      }
    }
  }

  async updateStock(categoryId, materialId, newStock) {
    try {
      const response = await fetch(`${this.baseUrl}/materials/${categoryId}/${materialId}/stock`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ stock: parseInt(newStock) })
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.showError('Nejste přihlášeni. Přihlaste se prosím jako administrátor.');
          return;
        }
        if (response.status === 403) {
          this.showError('Nemáte oprávnění k úpravě skladových zásob. Pouze administrátoři mohou upravovat tyto hodnoty.');
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        this.showSuccess(`Skladové množství bylo aktualizováno na ${newStock} ks`);
        // Aktualizovat lokální data
        const material = this.materials[categoryId].find(m => m.id === materialId);
        if (material) {
          material.skladem = parseInt(newStock);
        }
        // Aktualizovat formulář
        this.updateMaterialsForm();
      } else {
        throw new Error(result.error || 'Neznámá chyba');
      }
    } catch (error) {
      console.error('Chyba při aktualizaci skladového množství:', error);
      if (error.name === 'SyntaxError') {
        this.showError('Nejste přihlášeni nebo nemáte oprávnění. Přihlaste se prosím jako administrátor.');
      } else {
        this.showError(`Chyba při aktualizaci skladového množství: ${error.message}`);
      }
    }
  }

  async updateMinStock(categoryId, materialId, newMin) {
    try {
      const response = await fetch(`${this.baseUrl}/materials/${categoryId}/${materialId}/min-stock`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ minStock: parseInt(newMin) })
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.showError('Nejste přihlášeni. Přihlaste se prosím jako administrátor.');
          return;
        }
        if (response.status === 403) {
          this.showError('Nemáte oprávnění k úpravě min. skladu.');
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        this.showSuccess(`Min. sklad nastaven na ${newMin} ks`);
        const material = this.materials[categoryId].find(m => m.id === materialId);
        if (material) {
          material.minStock = parseInt(newMin);
        }
      } else {
        throw new Error(result.error || 'Neznámá chyba');
      }
    } catch (error) {
      console.error('Chyba při aktualizaci min. skladu:', error);
      this.showError(`Chyba při aktualizaci min. skladu: ${error.message}`);
    }
  }

  // ----- Skladové pohyby (warehouse) -----

  isMaterialLowStock(material) {
    const skladem = parseInt(material.skladem) || 0;
    const minStock = parseInt(material.minStock) || 0;
    return (minStock > 0 && skladem <= minStock) || skladem === 0;
  }

  computeLowStock() {
    const result = [];
    for (const categoryId of Object.keys(this.materials)) {
      const items = this.materials[categoryId];
      if (!Array.isArray(items)) continue;
      for (const material of items) {
        if (this.isMaterialLowStock(material)) {
          result.push({ categoryId, material });
        }
      }
    }
    return result;
  }

  toggleLowStockFilter() {
    this._lowStockOnly = !this._lowStockOnly;
    const label = document.getElementById('low-stock-filter-label');
    if (label) {
      label.textContent = this._lowStockOnly ? 'Zobrazit všechny' : 'Zobrazit pouze nízké';
    }
    const sections = document.querySelectorAll('#materials-container section[data-category]');
    sections.forEach((section) => {
      const rows = Array.from(section.querySelectorAll('tbody tr'));
      const lowRows = rows.filter((r) => r.getAttribute('data-low-stock') === 'true');
      const hasLow = lowRows.length > 0;
      if (!this._lowStockOnly) {
        section.style.display = '';
        rows.forEach((r) => { r.style.display = ''; });
      } else {
        if (!hasLow) {
          section.style.display = 'none';
        } else {
          section.style.display = '';
          rows.forEach((r) => {
            r.style.display = r.getAttribute('data-low-stock') === 'true' ? '' : 'none';
          });
        }
      }
    });
  }

  showMovementsModal(categoryId, materialId) {
    const material = this.materials[categoryId]?.find(m => m.id === materialId);
    if (!material) {
      this.showError('Materiál nebyl nalezen.');
      return;
    }

    const existing = document.getElementById('movementsModal');
    if (existing) existing.remove();

    this.createMovementsModal(material, categoryId);
    const tailwindModal = new TailwindModal('movementsModal');
    tailwindModal.show();

    this._movementsContext = { categoryId, materialId };
    this.loadMovements(categoryId, materialId);
  }

  createMovementsModal(material, categoryId) {
    const isAdmin = this.isAdmin();
    const available = (parseInt(material.skladem) || 0) - (parseInt(material.reservedCount) || 0);

    const modalHtml = `
      <div class="fixed inset-0 z-50 overflow-y-auto hidden" id="movementsModal" aria-labelledby="movementsModalLabel" aria-hidden="true">
        <div class="flex items-center justify-center min-h-screen px-4">
          <div class="fixed inset-0 bg-black opacity-50" id="modal-backdrop"></div>
          <div class="bg-white rounded-lg shadow-xl max-w-3xl w-full relative">
            <div class="px-6 py-4 border-b border-gray-200">
              <h3 class="text-lg font-medium text-gray-900" id="movementsModalLabel">Skladové pohyby – ${material.name}</h3>
              <button type="button" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600" id="modal-close-btn">
                ${icon('x-mark', 'h-6 w-6')}
              </button>
              <p class="text-sm text-gray-500 mt-1">
                Skladem: <strong>${material.skladem || 0}</strong> ·
                Rezervováno: <strong>${material.reservedCount || 0}</strong> ·
                Dostupné: <strong>${available}</strong> ·
                Min: <strong>${material.minStock || 0}</strong>
              </p>
            </div>
            <div class="px-6 py-4">
              ${isAdmin ? `
              <form id="movementForm" class="grid grid-cols-12 gap-3 items-end mb-4 p-3 bg-gray-50 rounded-md">
                <div class="col-span-3">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Typ pohybu</label>
                  <select id="movementType" class="block w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm">
                    <option value="in">Příjem</option>
                    <option value="out">Výdej</option>
                    <option value="reservation">Rezervace</option>
                  </select>
                </div>
                <div class="col-span-3">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Množství</label>
                  <input type="number" id="movementQuantity" min="1" value="1" class="block w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm">
                </div>
                <div class="col-span-3">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Objednávka (volitelné)</label>
                  <input type="text" id="movementOrderId" placeholder="ORD-..." class="block w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm">
                </div>
                <div class="col-span-3">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Důvod (volitelné)</label>
                  <input type="text" id="movementReason" class="block w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm">
                </div>
                <div class="col-span-12">
                  <button type="button" class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700" onclick="materialsAdmin.addMovement()">
                    ${icon('plus', 'w-4 h-4 mr-1')} Přidat pohyb
                  </button>
                </div>
              </form>
              ` : ''}
              <div id="movementsTableContainer" class="overflow-x-auto border border-gray-100 rounded-md">
                <div class="p-4 text-sm text-gray-500 text-center">Načítám pohyby…</div>
              </div>
            </div>
            <div class="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" id="modal-cancel-btn">Zavřít</button>
            </div>
          </div>
        </div>
      </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHtml);
  }

  async loadMovements(categoryId, materialId) {
    const container = document.getElementById('movementsTableContainer');
    if (!container) return;
    try {
      const url = new URL(`${this.baseUrl}/warehouse/movements`);
      url.searchParams.set('categoryId', categoryId);
      url.searchParams.set('materialId', materialId);
      const response = await fetch(url.toString(), { credentials: 'include' });
      const result = await response.json();
      if (result.success) {
        this.renderMovementsTable(result.data);
      } else {
        container.innerHTML = `<div class="p-4 text-sm text-red-600">${result.error || 'Chyba při načítání pohybů'}</div>`;
      }
    } catch (error) {
      console.error('Chyba při načítání pohybů:', error);
      container.innerHTML = `<div class="p-4 text-sm text-red-600">Chyba při načítání pohybů</div>`;
    }
  }

  renderMovementsTable(movements) {
    const container = document.getElementById('movementsTableContainer');
    if (!container) return;

    if (!movements || movements.length === 0) {
      container.innerHTML = `<div class="p-6 text-sm text-gray-500 text-center">Zatím žádné skladové pohyby.</div>`;
      return;
    }

    const typeLabel = (t) => ({
      'in': '<span class="text-green-700">Příjem</span>',
      'out': '<span class="text-red-700">Výdej</span>',
      'reservation': '<span class="text-amber-700">Rezervace</span>',
      'reservation-release': '<span class="text-ink-muted">Uvolnění rez.</span>'
    }[t] || t);

    const isAdmin = this.isAdmin();

    const rows = movements.map((m) => {
      const date = new Date(m.createdAt).toLocaleString('cs-CZ');
      const sign = m.type === 'in' ? '+' : (m.type === 'out' ? '−' : (m.type === 'reservation' ? '⋊' : '↺'));
      const released = m.type === 'reservation' && m.released ? ' <span class="text-[10px] text-ink-faint">(uvolněna)</span>' : '';
      return `
        <tr class="border-b border-gray-50">
          <td class="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">${date}</td>
          <td class="px-3 py-2 text-xs">${typeLabel(m.type)}${released}</td>
          <td class="px-3 py-2 text-xs tabular-nums font-medium">${sign} ${m.quantity}</td>
          <td class="px-3 py-2 text-xs text-gray-500">${m.orderId || '—'}</td>
          <td class="px-3 py-2 text-xs text-gray-500">${m.reason || '—'}</td>
          <td class="px-3 py-2 text-xs text-gray-500">${m.user || '—'}</td>
          <td class="px-3 py-2 text-xs text-right">
            ${isAdmin && m.type !== 'reservation-release' && !m.released
              ? `<button class="text-red-500 hover:text-red-700" onclick="materialsAdmin.deleteMovement('${m.id}')" title="Smazat pohyb">${icon('trash', 'w-3.5 h-3.5')}</button>`
              : ''}
          </td>
        </tr>
      `;
    }).join('');

    container.innerHTML = `
      <table class="min-w-full text-sm">
        <thead class="bg-gray-50 text-gray-500">
          <tr>
            <th class="px-3 py-2 text-left text-[11px] font-semibold uppercase">Datum</th>
            <th class="px-3 py-2 text-left text-[11px] font-semibold uppercase">Typ</th>
            <th class="px-3 py-2 text-left text-[11px] font-semibold uppercase">Množství</th>
            <th class="px-3 py-2 text-left text-[11px] font-semibold uppercase">Objednávka</th>
            <th class="px-3 py-2 text-left text-[11px] font-semibold uppercase">Důvod</th>
            <th class="px-3 py-2 text-left text-[11px] font-semibold uppercase">Uživatel</th>
            <th class="px-3 py-2 text-right text-[11px] font-semibold uppercase">Akce</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  async addMovement() {
    const ctx = this._movementsContext;
    if (!ctx) return;
    const type = document.getElementById('movementType').value;
    const quantity = parseInt(document.getElementById('movementQuantity').value);
    const orderId = document.getElementById('movementOrderId').value.trim();
    const reason = document.getElementById('movementReason').value.trim();

    if (!quantity || quantity <= 0) {
      this.showError('Množství musí být kladné celé číslo');
      return;
    }

    try {
      const response = await fetch(`${this.baseUrl}/warehouse/movements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          categoryId: ctx.categoryId,
          materialId: ctx.materialId,
          type,
          quantity,
          orderId: orderId || null,
          reason: reason || null
        })
      });
      const result = await response.json();
      if (result.success) {
        this.showSuccess('Skladový pohyb byl zaznamenán');
        document.getElementById('movementQuantity').value = '1';
        document.getElementById('movementOrderId').value = '';
        document.getElementById('movementReason').value = '';
        await this.loadMovements(ctx.categoryId, ctx.materialId);
        await this.loadMaterials();
        this.renderMaterials();
      } else {
        throw new Error(result.error || 'Neznámá chyba');
      }
    } catch (error) {
      console.error('Chyba při přidávání pohybu:', error);
      this.showError(`Chyba: ${error.message}`);
    }
  }

  async deleteMovement(movementId) {
    if (!confirm('Opravdu smazat tento skladový pohyb? Skladové stavy budou reverzně upraveny.')) return;
    const ctx = this._movementsContext;
    try {
      const response = await fetch(`${this.baseUrl}/warehouse/movements/${movementId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const result = await response.json();
      if (result.success) {
        this.showSuccess('Pohyb byl smazán');
        if (ctx) await this.loadMovements(ctx.categoryId, ctx.materialId);
        await this.loadMaterials();
        this.renderMaterials();
      } else {
        throw new Error(result.error || 'Neznámá chyba');
      }
    } catch (error) {
      console.error('Chyba při mazání pohybu:', error);
      this.showError(`Chyba: ${error.message}`);
    }
  }

  showAddMaterialModal() {
    try {
      const modal = document.getElementById('addMaterialModal');
      if (!modal) {
        this.createAddMaterialModal();
      }
      
      const tailwindModal = new TailwindModal('addMaterialModal');
      tailwindModal.show();
    } catch (error) {
      console.error('Chyba při otevírání modalu:', error);
      this.showError('Chyba při otevírání formuláře pro přidání materiálu.');
    }
  }

  createAddMaterialModal() {
    const modalHtml = `
      <div class="fixed inset-0 z-50 overflow-y-auto hidden" id="addMaterialModal" aria-labelledby="addMaterialModalLabel" aria-hidden="true">
        <div class="flex items-center justify-center min-h-screen px-4">
          <div class="fixed inset-0 bg-black opacity-50" id="modal-backdrop"></div>
          <div class="bg-white rounded-lg shadow-xl max-w-lg w-full relative">
            <div class="px-6 py-4 border-b border-gray-200">
              <h3 class="text-lg font-medium text-gray-900" id="addMaterialModalLabel">Přidat nový materiál</h3>
              <button type="button" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600" id="modal-close-btn">
                ${icon('x-mark', 'h-6 w-6')}
              </button>
            </div>
            <div class="px-6 py-4">
              <form id="addMaterialForm">
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Kategorie</label>
                  <select class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white" id="materialCategory" required>
                    <option value="">Vyberte kategorii</option>
                    <option value="ofset">Ofsetový papír</option>
                    <option value="laserova">Do laserové tiskárny</option>
                    <option value="krida-leskla">Lesklá křída</option>
                    <option value="krida-matna">Matná křída</option>
                    <option value="recyklovane">Recyklované a ekologické papíry</option>
                    <option value="lepenky">Lepenky</option>
                    <option value="samolepici">Samolepící papíry</option>
                    <option value="kartony">Kartóny</option>
                    <option value="specialni">Speciální grafické papíry</option>
                  </select>
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Název</label>
                  <input type="text" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="materialName" required minlength="2" maxlength="100" placeholder="Zadejte název materiálu">
                  <div class="text-red-500 text-sm mt-1 hidden" id="materialNameError">Název musí mít alespoň 2 znaky</div>
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Cena (Kč)</label>
                  <input type="number" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="materialPrice" step="0.01" min="0.01" max="99999" required placeholder="0.00">
                  <div class="text-red-500 text-sm mt-1 hidden" id="materialPriceError">Cena musí být větší než 0 Kč</div>
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Skladem (ks)</label>
                  <input type="number" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="materialStock" min="0" value="0" placeholder="0">
                  <div class="text-red-500 text-sm mt-1 hidden" id="materialStockError">Skladové množství musí být 0 nebo více</div>
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Gramáž (g/m²)</label>
                  <input type="number" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="materialWeight" min="10" max="1000" required placeholder="80">
                  <div class="text-red-500 text-sm mt-1 hidden" id="materialWeightError">Gramáž musí být mezi 10-1000 g/m²</div>
                </div>
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Šířka (mm)</label>
                    <input type="number" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="materialWidth" value="650" min="100" max="5000" required>
                    <div class="text-red-500 text-sm mt-1 hidden" id="materialWidthError">Šířka musí být 100-5000 mm</div>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Výška (mm)</label>
                    <input type="number" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="materialHeight" value="480" min="100" max="5000" required>
                    <div class="text-red-500 text-sm mt-1 hidden" id="materialHeightError">Výška musí být 100-5000 mm</div>
                  </div>
                </div>
                <div class="mb-4">
                  <div class="flex items-center">
                    <input type="checkbox" id="materialCanFlip" checked class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded">
                    <label class="ml-2 text-sm text-gray-700" for="materialCanFlip">
                      Umožnit obrátku
                    </label>
                  </div>
                </div>
              </form>
            </div>
            <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" id="modal-cancel-btn">Zrušit</button>
              <button type="button" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onclick="materialsAdmin.addMaterial()">Přidat</button>
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHtml);
    
    // Nastavit validaci pro formulář
    this.setupFormValidation();
  }

  setupFormValidation() {
    const fields = [
      { id: 'materialName', errorId: 'materialNameError', validation: (value) => value.length >= 2 },
      { id: 'materialPrice', errorId: 'materialPriceError', validation: (value) => parseFloat(value) > 0 },
      { id: 'materialStock', errorId: 'materialStockError', validation: (value) => parseInt(value) >= 0 },
      { id: 'materialWeight', errorId: 'materialWeightError', validation: (value) => parseInt(value) >= 10 && parseInt(value) <= 1000 },
      { id: 'materialWidth', errorId: 'materialWidthError', validation: (value) => parseInt(value) >= 100 && parseInt(value) <= 5000 },
      { id: 'materialHeight', errorId: 'materialHeightError', validation: (value) => parseInt(value) >= 100 && parseInt(value) <= 5000 }
    ];

    fields.forEach(field => {
      const input = document.getElementById(field.id);
      const errorDiv = document.getElementById(field.errorId);
      
      if (input && errorDiv) {
        // Validace při blur (opuštění pole)
        input.addEventListener('blur', () => {
          const isValid = field.validation(input.value);
          if (!isValid && input.value !== '') {
            errorDiv.classList.remove('hidden');
            input.classList.add('border-red-500');
            input.classList.remove('border-gray-300');
          } else {
            errorDiv.classList.add('hidden');
            input.classList.remove('border-red-500');
            input.classList.add('border-gray-300');
          }
        });

        // Skrýt chybu při začátku psaní
        input.addEventListener('input', () => {
          if (!errorDiv.classList.contains('hidden')) {
            const isValid = field.validation(input.value);
            if (isValid) {
              errorDiv.classList.add('hidden');
              input.classList.remove('border-red-500');
              input.classList.add('border-gray-300');
            }
          }
        });
      }
    });
  }

  async addMaterial() {
    const form = document.getElementById('addMaterialForm');
    
    // Kontrola HTML5 validace
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    
    const materialData = {
      name: document.getElementById('materialName').value.trim(),
      price: parseFloat(document.getElementById('materialPrice').value),
      skladem: parseInt(document.getElementById('materialStock').value),
      weight: parseInt(document.getElementById('materialWeight').value),
      width: parseInt(document.getElementById('materialWidth').value),
      height: parseInt(document.getElementById('materialHeight').value),
      canFlip: document.getElementById('materialCanFlip').checked
    };

    const categoryId = document.getElementById('materialCategory').value;

    // Detailní validace
    const validationErrors = [];
    
    if (!categoryId) {
      validationErrors.push('Vyberte kategorii materiálu');
    }
    
    if (!materialData.name || materialData.name.length < 2) {
      validationErrors.push('Název musí mít alespoň 2 znaky');
    }
    
    if (!materialData.price || materialData.price <= 0) {
      validationErrors.push('Cena musí být větší než 0 Kč');
    }
    
    if (materialData.skladem < 0) {
      validationErrors.push('Skladové množství musí být 0 nebo více');
    }
    
    if (!materialData.weight || materialData.weight < 10 || materialData.weight > 1000) {
      validationErrors.push('Gramáž musí být mezi 10-1000 g/m²');
    }
    
    if (!materialData.width || materialData.width < 100 || materialData.width > 5000) {
      validationErrors.push('Šířka musí být mezi 100-5000 mm');
    }
    
    if (!materialData.height || materialData.height < 100 || materialData.height > 5000) {
      validationErrors.push('Výška musí být mezi 100-5000 mm');
    }

    if (validationErrors.length > 0) {
      this.showError('Opravte následující chyby:\n• ' + validationErrors.join('\n• '));
      return;
    }

    try {
      const response = await fetch(`${this.baseUrl}/materials/${categoryId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(materialData)
      });

      const result = await response.json();
      
      if (result.success) {
        this.showSuccess('Materiál byl úspěšně přidán');
        const tailwindModal = new TailwindModal('addMaterialModal');
        tailwindModal.hide();
        form.reset();
        await this.loadMaterials();
        this.renderMaterials();
        // Aktualizovat formulář
        this.updateMaterialsForm();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Chyba při přidávání materiálu:', error);
      this.showError('Chyba při přidávání materiálu');
    }
  }

  async deleteMaterial(categoryId, materialId) {
    if (!confirm('Opravdu chcete smazat tento materiál?')) {
      return;
    }

    try {
      const response = await fetch(`${this.baseUrl}/materials/${categoryId}/${materialId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const result = await response.json();
      
      if (result.success) {
        this.showSuccess('Materiál byl úspěšně smazán');
        await this.loadMaterials();
        this.renderMaterials();
        // Aktualizovat formulář
        this.updateMaterialsForm();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Chyba při mazání materiálu:', error);
      this.showError('Chyba při mazání materiálu');
    }
  }

  async exportMaterials(format = 'csv') {
    try {
      const response = await fetch(`${this.baseUrl}/materials/export?format=${format}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Nastavení názvu souboru podle formátu
      const fileExtension = format === 'csv' ? 'csv' : 'json';
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      a.download = `materials-export-${timestamp}.${fileExtension}`;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      const formatName = format === 'csv' ? 'CSV' : 'JSON';
      this.showSuccess(`Data byla exportována do ${formatName} souboru`);
    } catch (error) {
      console.error('Chyba při exportu:', error);
      this.showError('Chyba při exportu dat: ' + error.message);
    }
  }

  // Nová funkce pro zobrazení modalu s výběrem formátu exportu
  showExportModal() {
    const modalHtml = `
      <div class="fixed inset-0 z-50 overflow-y-auto hidden" id="exportModal" aria-labelledby="exportModalLabel" aria-hidden="true">
        <div class="flex items-center justify-center min-h-screen px-4">
          <div class="fixed inset-0 bg-black opacity-50" id="modal-backdrop"></div>
          <div class="bg-white rounded-lg shadow-xl max-w-md w-full relative">
            <div class="px-6 py-4 border-b border-gray-200">
              <h3 class="text-lg font-medium text-gray-900" id="exportModalLabel">Export materiálů</h3>
              <button type="button" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600" id="modal-close-btn">
                ${icon('x-mark', 'h-6 w-6')}
              </button>
            </div>
            <div class="px-6 py-4">
              <p class="text-sm text-gray-600 mb-4">Vyberte formát pro export materiálů:</p>
              
              <div class="space-y-3">
                <label class="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input type="radio" name="exportFormat" value="csv" checked class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300">
                  <div class="ml-3">
                    <div class="font-medium text-gray-900">CSV soubor</div>
                    <div class="text-sm text-gray-500">Vhodné pro Excel a tabulkové procesory</div>
                  </div>
                </label>
                
                <label class="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input type="radio" name="exportFormat" value="json" class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300">
                  <div class="ml-3">
                    <div class="font-medium text-gray-900">JSON soubor</div>
                    <div class="text-sm text-gray-500">Zachovává kompletní strukturu dat</div>
                  </div>
                </label>
              </div>
            </div>
            <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" id="modal-cancel-btn">Zrušit</button>
              <button type="button" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onclick="materialsAdmin.executeExport()">Exportovat</button>
            </div>
          </div>
        </div>
      </div>
    `;
    
    // Odebrání existujícího modalu
    const existingModal = document.getElementById('exportModal');
    if (existingModal) {
      existingModal.remove();
    }
    
    document.body.insertAdjacentHTML('beforeend', modalHtml);
    const tailwindModal = new TailwindModal('exportModal');
    tailwindModal.show();
  }

  executeExport() {
    const selectedFormat = document.querySelector('input[name="exportFormat"]:checked').value;
    const tailwindModal = new TailwindModal('exportModal');
    tailwindModal.hide();
    this.exportMaterials(selectedFormat);
  }

  showImportModal() {
    // Implementace import modalu
    const modalHtml = `
      <div class="fixed inset-0 z-50 overflow-y-auto hidden" id="importModal" aria-labelledby="importModalLabel" aria-hidden="true">
        <div class="flex items-center justify-center min-h-screen px-4">
          <div class="fixed inset-0 bg-black opacity-50" id="modal-backdrop"></div>
          <div class="bg-white rounded-lg shadow-xl max-w-lg w-full relative">
            <div class="px-6 py-4 border-b border-gray-200">
              <h3 class="text-lg font-medium text-gray-900" id="importModalLabel">Import materiálů</h3>
              <button type="button" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600" id="modal-close-btn">
                ${icon('x-mark', 'h-6 w-6')}
              </button>
            </div>
            <div class="px-6 py-4">
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Vyberte soubor</label>
                <input type="file" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="importFile" accept=".csv,.json">
                <p class="mt-2 text-sm text-gray-500">Podporované formáty: CSV a JSON</p>
              </div>
              
              <div class="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                <h4 class="text-sm font-medium text-blue-800 mb-2">Formát CSV souboru:</h4>
                <p class="text-xs text-blue-700">
                  Soubor musí obsahovat sloupce: Kategorie, Název, Cena (Kč), Skladem, Gramáž (g/m²), Šířka (mm), Výška (mm), Obrátka<br>
                  Oddělovač: středník (;)<br>
                  Kódování: UTF-8
                </p>
              </div>
              
              <div class="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div class="flex">
                  ${icon('exclamation-triangle', 'flex-shrink-0 h-5 w-5 text-yellow-400')}
                  <div class="ml-3">
                    <h3 class="text-sm font-medium text-yellow-800">Upozornění</h3>
                    <div class="mt-2 text-sm text-yellow-700">
                      Import nahradí všechny stávající materiály. Doporučujeme nejprve vyexportovat zálohu.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" id="modal-cancel-btn">Zrušit</button>
              <button type="button" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onclick="materialsAdmin.importMaterials()">Importovat</button>
            </div>
          </div>
        </div>
      </div>
    `;
    
    // Odebrání existujícího modalu
    const existingModal = document.getElementById('importModal');
    if (existingModal) {
      existingModal.remove();
    }
    
    document.body.insertAdjacentHTML('beforeend', modalHtml);
    const tailwindModal = new TailwindModal('importModal');
    tailwindModal.show();
  }

  async importMaterials() {
    const fileInput = document.getElementById('importFile');
    const file = fileInput.files[0];
    
    if (!file) {
      this.showError('Vyberte soubor');
      return;
    }

    try {
      const text = await file.text();
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      let requestHeaders = {};
      let requestBody;
      
      let endpoint;
      
      if (fileExtension === 'csv') {
        // CSV import - specializovaný endpoint
        requestHeaders['Content-Type'] = 'text/csv; charset=utf-8';
        requestBody = text;
        endpoint = `${this.baseUrl}/materials/import-csv`;
      } else {
        // JSON import (zpětná kompatibilita)
        try {
          const data = JSON.parse(text);
          requestHeaders['Content-Type'] = 'application/json';
          requestBody = JSON.stringify(data);
          endpoint = `${this.baseUrl}/materials/import`;
        } catch (parseError) {
          this.showError('Neplatný JSON formát souboru');
          return;
        }
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: requestHeaders,
        credentials: 'include',
        body: requestBody
      });

      const result = await response.json();
      
      if (result.success) {
        const formatName = fileExtension === 'csv' ? 'CSV' : 'JSON';
        this.showSuccess(`Data byla úspěšně importována z ${formatName} souboru`);
        const tailwindModal = new TailwindModal('importModal');
        tailwindModal.hide();
        await this.loadMaterials();
        this.renderMaterials();
        // Aktualizovat formulář
        this.updateMaterialsForm();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Chyba při importu:', error);
      this.showError('Chyba při importu dat: ' + error.message);
    }
  }

  showSuccess(message) {
    this.showToast(message, 'success');
  }

  showError(message) {
    this.showToast(message, 'error');
  }

  showToast(message, type) {
    // Jednoduchý toast systém
    const toastContainer = document.getElementById('toast-container') || this.createToastContainer();
    
    const toast = document.createElement('div');
    const bgColor = type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800';
    toast.className = `border rounded-md p-4 mb-2 ${bgColor} flex items-center justify-between`;
    toast.innerHTML = `
      <span>${message}</span>
      <button type="button" class="text-gray-400 hover:text-gray-600" onclick="this.parentElement.remove()">
        ${icon('x-mark', 'h-4 w-4')}
      </button>
    `;
    
    toastContainer.appendChild(toast);
    
    // Automatické odstranění po 5 sekundách
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 5000);
  }

  createToastContainer() {
    const container = document.createElement('div');
    container.id = 'toast-container';
    container.style.position = 'fixed';
    container.style.top = '20px';
    container.style.right = '20px';
    container.style.zIndex = '9999';
    document.body.appendChild(container);
    return container;
  }

  // Upravit materiál
  editMaterial(categoryId, materialId) {
    try {
      // Najdeme materiál podle categoryId a materialId
      const material = this.materials[categoryId]?.find(m => m.id === materialId);
      if (!material) {
        this.showError('Materiál nebyl nalezen.');
        return;
      }

      // Vytvoříme/otevřeme edit modal
      const modal = document.getElementById('editMaterialModal');
      if (!modal) {
        this.createEditMaterialModal();
      }
      
      // Předvyplníme formulář
      this.populateEditForm(material, categoryId);
      
      const tailwindModal = new TailwindModal('editMaterialModal');
      tailwindModal.show();
    } catch (error) {
      console.error('Chyba při otevírání editačního modalu:', error);
      this.showError('Chyba při otevírání formuláře pro úpravu materiálu.');
    }
  }

  // Smazat materiál
  async deleteMaterial(categoryId, materialId) {
    try {
      const material = this.materials[categoryId]?.find(m => m.id === materialId);
      if (!material) {
        this.showError('Materiál nebyl nalezen.');
        return;
      }

      if (!confirm(`Opravdu chcete smazat materiál "${material.name}"?`)) {
        return;
      }

      const response = await fetch(`${this.baseUrl}/materials/${categoryId}/${materialId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const result = await response.json();
      
      if (result.success) {
        this.showSuccess('Materiál byl úspěšně smazán.');
        await this.loadMaterials();
        this.renderMaterials();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Chyba při mazání materiálu:', error);
      this.showError('Chyba při mazání materiálu: ' + error.message);
    }
  }

  // Vytvořit edit modal
  createEditMaterialModal() {
    const modalHtml = `
      <div class="fixed inset-0 z-50 overflow-y-auto hidden" id="editMaterialModal" aria-labelledby="editMaterialModalLabel" aria-hidden="true">
        <div class="flex items-center justify-center min-h-screen px-4">
          <div class="fixed inset-0 bg-black opacity-50" id="modal-backdrop"></div>
          <div class="bg-white rounded-lg shadow-xl max-w-lg w-full relative">
            <div class="px-6 py-4 border-b border-gray-200">
              <h3 class="text-lg font-medium text-gray-900" id="editMaterialModalLabel">Upravit materiál</h3>
              <button type="button" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600" id="modal-close-btn">
                ${icon('x-mark', 'h-6 w-6')}
              </button>
            </div>
            <div class="px-6 py-4">
              <form id="editMaterialForm">
                <input type="hidden" id="editMaterialId" name="materialId">
                <input type="hidden" id="editCategoryId" name="categoryId">
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Kategorie</label>
                  <select class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white" id="editMaterialCategory" required>
                    <option value="">Vyberte kategorii</option>
                    <option value="ofset">Ofsetový papír</option>
                    <option value="laserova">Do laserové tiskárny</option>
                    <option value="krida-leskla">Lesklá křída</option>
                    <option value="krida-matna">Matná křída</option>
                    <option value="recyklovane">Recyklované a ekologické papíry</option>
                    <option value="lepenky">Lepenky</option>
                    <option value="samolepici">Samolepící papíry</option>
                    <option value="kartony">Kartóny</option>
                    <option value="specialni">Speciální grafické papíry</option>
                  </select>
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Název</label>
                  <input type="text" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="editMaterialName" required>
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Cena (Kč)</label>
                  <input type="number" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="editMaterialPrice" step="0.01" min="0" required>
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Skladem (ks)</label>
                  <input type="number" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="editMaterialStock" min="0" value="0">
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Gramáž (g/m²)</label>
                  <input type="number" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="editMaterialWeight" min="1" required>
                </div>
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Šířka (mm)</label>
                    <input type="number" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="editMaterialWidth" min="1" required>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Výška (mm)</label>
                    <input type="number" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" id="editMaterialHeight" min="1" required>
                  </div>
                </div>
                <div class="mb-4">
                  <div class="flex items-center">
                    <input type="checkbox" id="editMaterialCanFlip" class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded">
                    <label class="ml-2 text-sm text-gray-700" for="editMaterialCanFlip">
                      Umožnit obrátku
                    </label>
                  </div>
                </div>
              </form>
            </div>
            <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" id="modal-cancel-btn">Zrušit</button>
              <button type="button" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onclick="materialsAdmin.updateMaterial()">Uložit změny</button>
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHtml);
  }

  // Předvyplnit edit formulář
  populateEditForm(material, categoryId) {
    document.getElementById('editMaterialId').value = material.id;
    document.getElementById('editCategoryId').value = categoryId;
    document.getElementById('editMaterialCategory').value = categoryId;
    document.getElementById('editMaterialName').value = material.name;
    document.getElementById('editMaterialPrice').value = material.price;
    document.getElementById('editMaterialStock').value = material.skladem || 0;
    document.getElementById('editMaterialWeight').value = material.weight;
    document.getElementById('editMaterialWidth').value = material.width;
    document.getElementById('editMaterialHeight').value = material.height;
    document.getElementById('editMaterialCanFlip').checked = material.canFlip;
  }

  // Aktualizovat materiál
  async updateMaterial() {
    try {
      const materialId = document.getElementById('editMaterialId').value;
      const oldCategoryId = document.getElementById('editCategoryId').value;
      const newCategoryId = document.getElementById('editMaterialCategory').value;
      
      const materialData = {
        id: materialId,
        name: document.getElementById('editMaterialName').value,
        price: parseFloat(document.getElementById('editMaterialPrice').value),
        skladem: parseInt(document.getElementById('editMaterialStock').value),
        weight: parseInt(document.getElementById('editMaterialWeight').value),
        width: parseInt(document.getElementById('editMaterialWidth').value),
        height: parseInt(document.getElementById('editMaterialHeight').value),
        canFlip: document.getElementById('editMaterialCanFlip').checked
      };

      // Validace
      if (!materialData.name || materialData.price < 0 || materialData.weight < 1 || materialData.width < 1 || materialData.height < 1) {
        this.showError('Prosím vyplňte všechna povinná pole správně.');
        return;
      }

      // Pokud se změnila kategorie, potřebujeme smazat z původní a přidat do nové
      if (oldCategoryId !== newCategoryId) {
        // Smazat z původní kategorie
        await fetch(`${this.baseUrl}/materials/${oldCategoryId}/${materialId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        
        // Přidat do nové kategorie
        const response = await fetch(`${this.baseUrl}/materials/${newCategoryId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(materialData)
        });
        
        const result = await response.json();
        
        if (result.success) {
          this.showSuccess('Materiál byl úspěšně přesunut do nové kategorie a aktualizován.');
        } else {
          throw new Error(result.error);
        }
      } else {
        // Aktualizovat ve stejné kategorii
        const response = await fetch(`${this.baseUrl}/materials/${oldCategoryId}/${materialId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(materialData)
        });
        
        const result = await response.json();
        
        if (result.success) {
          this.showSuccess('Materiál byl úspěšně aktualizován.');
        } else {
          throw new Error(result.error);
        }
      }
      
      // Zavřít modal
      const tailwindModal = new TailwindModal('editMaterialModal');
      tailwindModal.hide();
      
      // Znovu načíst a zobrazit materiály
      await this.loadMaterials();
      this.renderMaterials();
      // Aktualizovat formulář
      this.updateMaterialsForm();
      
    } catch (error) {
      console.error('Chyba při aktualizaci materiálu:', error);
      this.showError('Chyba při aktualizaci materiálu: ' + error.message);
    }
  }



  // Inicializace tooltipů
  initTooltips() {
    // Tooltip funkcionalita bude implementována později s Tailwind
    // Prozatím ponecháme prázdné
  }

  // Funkce pro aktualizaci formuláře materiálů
  updateMaterialsForm() {
    if (typeof window.loadMaterialsToForm === 'function') {
      window.loadMaterialsToForm();
    } else {
      // Fallback - pokud funkce není dostupná, aktualizujeme přímo
      this.renderMaterialsToSelect();
    }
  }

  // Funkce pro renderování materiálů do select elementu (fallback)
  renderMaterialsToSelect() {
    const select = document.getElementById('material');
    if (!select) return;
    
    // Zjistím aktuálně vybranou hodnotu
    const selectedValue = select.value;
    
    // Vyčistím select
    select.innerHTML = '';
    
    // Kategorie a jejich názvy
    const categoryNames = {
      'ofset': 'Ofsetový papír',
      'laserova': 'Do laserové tiskárny',
      'krida-leskla': 'Lesklá křída',
      'krida-matna': 'Matná křída',
      'recyklovane': 'Recyklované a ekologické papíry',
      'lepenky': 'Lepenky',
      'samolepici': 'Samolepící papíry',
      'kartony': 'Kartóny',
      'specialni': 'Speciální grafické papíry'
    };
    
    // Pořadí kategorií
    const categoryOrder = ['ofset', 'laserova', 'krida-leskla', 'krida-matna', 'recyklovane', 'lepenky', 'samolepici', 'kartony', 'specialni'];
    
    // Vytvořím optgroup pro každou kategorii
    categoryOrder.forEach(categoryId => {
      const categoryMaterials = this.materials[categoryId];
      if (!categoryMaterials || categoryMaterials.length === 0) return;
      
      const optgroup = document.createElement('optgroup');
      optgroup.label = categoryNames[categoryId];
      
      categoryMaterials.forEach(material => {
        const option = document.createElement('option');
        option.value = material.price;
        option.textContent = material.name;
        option.setAttribute('data-obratka', material.canFlip ? '1' : '0');
        option.setAttribute('data-gramaz', material.weight);
        option.setAttribute('data-vyska', material.width);  // Pozor: width je výška archu
        option.setAttribute('data-sirka', material.height); // Pozor: height je šířka archu
        
        // Pokud je to stejná hodnota jako předchozí selected, označím jako selected
        if (selectedValue && selectedValue === material.price.toString()) {
          option.selected = true;
        }
        
        optgroup.appendChild(option);
      });
      
      select.appendChild(optgroup);
    });
    
    // Přidám možnost "Vlastní materiál" na konec
    const customOption = document.createElement('option');
    customOption.value = 'vlastni';
    customOption.textContent = 'Vlastní materiál';
    customOption.setAttribute('data-obratka', '1');
    customOption.setAttribute('data-gramaz', '200');
    customOption.setAttribute('data-vyska', '650');
    customOption.setAttribute('data-sirka', '480');
    
    select.appendChild(customOption);
    
    // Pokud nebyla žádná hodnota vybrána, vyberu výchozí (Ofset 80 g/m²)
    if (!selectedValue) {
      const defaultOption = select.querySelector('option[value="1.43"]');
      if (defaultOption) {
        defaultOption.selected = true;
      }
    }
  }
}

// Export třídy pro použití v app.js
export { MaterialsAdmin }; 